import type { ApiResponse } from '../types';
import { throwIfAuthFailure } from './authError';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Shared fetch wrapper for the Functions API. Single place for the base URL,
 * JSON headers, `ApiResponse<T>` unwrapping, and auth-expiry handling — so a
 * change to any of those is made once instead of in every api/* module.
 *
 * Pass an `AbortSignal` via `options.signal` (e.g. TanStack Query's queryFn
 * signal) to make a request cancellable.
 */
export async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  return (await fetchApiEnvelope<ApiResponse<T>>(endpoint, options)).data!;
}

/**
 * The same request as {@link fetchApi}, returning the WHOLE response body instead of only `data`, for the
 * few endpoints that answer facts beside it (the period edit's `adjusted` / `adjustmentsFailed`). One code
 * path, so headers, auth expiry and error handling cannot drift between the two.
 */
export async function fetchApiEnvelope<E extends { success: boolean; error?: string }>(
  endpoint: string,
  options?: RequestInit
): Promise<E> {
  const { headers, ...rest } = options ?? {};

  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    ...rest,
  });

  // A 401/403 means the MSAL bearer token was missing, expired, or rejected by the backend.
  // Surface it as an error (TanStack Query retries once); re-auth is handled by MSAL via
  // acquireTokenSilent and the App's auth gate. Do NOT hard-redirect to SWA EasyAuth
  // (/.auth/login/aad) — that is a different auth system than the JWT the backend validates,
  // so bouncing there never yields a valid token and produces an infinite sign-in loop when
  // the token cannot be silently renewed.
  throwIfAuthFailure(response);

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`API error: ${response.status} - ${error}`);
  }

  const result: E = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Unknown error');
  }

  return result;
}
